import { NextRouter } from "next/router";
import { PersistPartial } from "redux-persist/es/persistReducer";
import { confirmReceiptRequest } from "../services/hooks/hooksHelper/usePayHelper";
import { AppDispatch } from "../services/redux/app/store";
import { InitialState } from "../services/redux/slices/deliverySlice";
import {
  LoadingActionEnum,
  increasePendingOperations,
  pushToLoadingStack,
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
  setLoading,
} from "../services/redux/slices/globalComponentsToggleSlice";
import { setLoyaltyEarnedPoints } from "../services/redux/slices/receiptSlice";
import { CartItemType } from "../services/redux/types/CartLoyaltyV2Types";
import { OrderCartV2Output } from "../services/redux/types/OrderCartV2Types";
import env from "../utils/env.config";
import LS from "./LocalStorage";
import {
  LocalStorageEnum,
  OnlinePaymentProvider,
  OrderTypesEnum,
  PAYMENT_METHOD,
  PagesEnum,
  ReceiptSourceEnum,
  ReceiptSourceTypeEnum,
  URL_PARAMS,
} from "./enums";
import { getFranchiseSlug, getPageLink } from "./help/navigation/navigation";
import { getOrderTypeKeyStringByValue } from "./help/orderType/orderType";
import { validateTableForFranchise } from "./help/table/tableValidation";
import { Franchise, Restaurant } from "./types";
import {
  CreateReceiptAppliedOffer,
  CreateReceiptBodyTypeFlat,
  CreateReceiptOrderAddon,
  CreateReceiptOrderLine,
} from "./types/createReceipt";
import type { FranchiseDeliveryConfig } from "./types";

/**
 * Builds the loyaltyV2 create-receipt order lines from the display cart items
 * (which carry numeric ids, addons, version, excludeFromOffers and lineId) and
 * joins per-line pricing from the latest cart-calculate result by lineId.
 */
const buildCreateReceiptItems = (
  cartItems: CartItemType[],
  loyaltyResult: OrderCartV2Output
): CreateReceiptOrderLine[] => {
  const pricingByLineId = new Map(
    loyaltyResult.lines.map((line) => [line.lineId, line.pricing])
  );

  const items: CreateReceiptOrderLine[] = [];

  cartItems.forEach((item) => {
    if (!item.lineId || item.subItemDetails?.id == null) {
      return;
    }

    // Addons mirror exactly what the loyaltyV2 cart-calculate sends (nested
    // addons nest under their parent's `nestedAddons`, and that is what priced
    // the cart), using the AddonGroupSubItem ROW id (`addonGroupSubItem.id`,
    // which the backend resolves via getValidAddonGroupSubItemWithSubItems).
    // Walking `addonGroupSubItem.subItem.allAddonGroups` is safe here: the
    // display reconstruction (applyAddonsToSubItem) rebuilds that sub-tree from
    // the order line so it holds only *selected* nested addons, never the menu
    // template (all available options), which would over-price the receipt.
    const addons: CreateReceiptOrderAddon[] = [];
    item.subItemDetails?.allAddonGroups?.forEach((addOn) => {
      addOn.addonGroupSubItem?.forEach((addonGroupSubItem) => {
        if (addonGroupSubItem.id == null) {
          return;
        }

        const nestedAddons: CreateReceiptOrderAddon["nestedAddons"] = [];
        addonGroupSubItem.subItem?.allAddonGroups?.forEach((nestedGroup) => {
          nestedGroup.addonGroupSubItem?.forEach((nestedAddon) => {
            if (nestedAddon.id == null) {
              return;
            }

            nestedAddons.push({
              id: nestedAddon.id,
              count: nestedAddon.count || 1,
            });
          });
        });

        addons.push({
          id: addonGroupSubItem.id,
          count: addonGroupSubItem.count || 1,
          nestedAddons,
        });
      });
    });

    const linePricing = pricingByLineId.get(item.lineId);

    items.push({
      lineId: item.lineId,
      subItemId: item.subItemDetails.id,
      count: item.count,
      note: item.note,
      addons,
      version: item.version,
      excludeFromOffers: item.excludeFromOffers ?? false,
      pricing: {
        total: linePricing?.total ?? item.totalPrice,
        subtotal: linePricing?.subtotal,
        discount: linePricing?.discount,
      },
    });
  });

  return items;
};

const mapCartPricing = (
  loyaltyResult: OrderCartV2Output
): CreateReceiptBodyTypeFlat["pricing"] => {
  const { pricing } = loyaltyResult;

  return {
    subtotal: pricing.subtotal,
    discounts: pricing.discounts,
    serviceCharge: pricing.serviceCharge,
    tax: pricing.tax,
    delivery: pricing.delivery,
    ...(pricing.totalBeforePayWithPoints !== undefined && {
      totalBeforePayWithPoints: pricing.totalBeforePayWithPoints,
    }),
    ...(pricing.payWithPoints?.applied && {
      payWithPoints: pricing.payWithPoints.applied,
    }),
    total: pricing.total,
  };
};

type handleCreateReceiptParams = {
  pageURL: string;
  paymentMethod: PAYMENT_METHOD;
  router: NextRouter;
  orderType: OrderTypesEnum;
  dispatch: AppDispatch;
  deliveryAddress: InitialState & PersistPartial;
  /**
   * Derived, not read off the delivery slice.
   *
   * The slice stores only WHICH config the guest picked; the config itself and
   * the effective fee come from the franchise the page carries. See
   * redux/selectors/deliverySelectors. prepits/project#4204
   */
  currentDeliveryConfig: FranchiseDeliveryConfig | null;
  hasRoundingMethodAndLevel: boolean;
  tableNumber: number;
  franchise: string;
  referenceName: string;
  restaurantInfo: Restaurant | null;
  onlinePaymentProvider?: OnlinePaymentProvider | undefined;
  tips?: number;
  savedCard?: any;
  isVisitor: boolean;
  redirectPage: PagesEnum;
  cartItems: CartItemType[];
  loyaltyResult?: OrderCartV2Output;
  appliedOffer?: CreateReceiptAppliedOffer;
  receiptNote?: string;
  scheduledSlotId?: number;
  numberOfGuests?: number;
  scheduledPickup?: string;
  selfService?: boolean;
  /**
   * Take the order from the customer's wallet balance first. The backend works
   * out how much (`min(balance, total with tip)`), so only the intent is sent.
   */
  useWallet?: boolean;
  /**
   * The guest turned DSQ partner points on (prepits/project#4437): they pick a
   * partner and redeem in the DSQ webview, and the rest is paid online.
   */
  payWithPartnerPoints?: boolean;
};

export const handleCreateReceipt = async ({
  pageURL,
  paymentMethod,
  router,
  orderType,
  dispatch,
  deliveryAddress,
  currentDeliveryConfig,
  tableNumber,
  franchise,
  referenceName,
  restaurantInfo,
  onlinePaymentProvider,
  tips,
  savedCard,
  isVisitor,
  redirectPage,
  cartItems,
  loyaltyResult,
  appliedOffer,
  receiptNote,
  scheduledSlotId,
  numberOfGuests,
  scheduledPickup,
  selfService,
  useWallet,
  payWithPartnerPoints,
}: handleCreateReceiptParams) => {
  const loadingStateSolution = restaurantInfo?.config?.loadingStateSolution;

  const startLoading = () => {
    if (loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.INCREASE,
          functionName: "handleCreateReceipt",
          date: new Date(),
        })
      );
      dispatch(increasePendingOperations());
    } else {
      dispatch(setLoading(true));
    }
  };

  const stopLoading = () => {
    if (loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.DECREASE,
          functionName: "handleCreateReceipt",
          date: new Date(),
        })
      );
    } else {
      dispatch(setLoading(false));
    }
  };

  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    window.location.href = window.location.href.replace(
      pageURL,
      `/${PagesEnum.Cart}?${URL_PARAMS.EMPTY_CART}=true`
    );

    return;
  }

  startLoading();

  // Fail-closed: the body's pricing must come from a fresh calculate result.
  // Without it we cannot quote the order — bail and let the cart recalculate.
  if (!loyaltyResult?.pricing) {
    stopLoading();
    dispatch(
      setAnimatedGlobalBannerState({
        isOpen: true,
        text: "Please try again",
        warning: true,
      })
    );

    return;
  }

  tips = tips || 0;

  if (isVisitor && !LS._get(LocalStorageEnum.DEVICE_NUMBER)) {
    stopLoading();
    router.push(
      getPageLink(router, PagesEnum.Login, {
        queryParams: [`${URL_PARAMS.PAY}=true`],
      }).as
    );

    return;
  }

  let success_url = window.location.href.replace(pageURL, "/pay");
  success_url = success_url.replace(`&${URL_PARAMS.PAY}=true`, "");
  let cancel_url = window.location.href.replace(pageURL, "/cart");
  cancel_url = cancel_url.replace(
    `&${URL_PARAMS.PAY}=true`,
    `&${URL_PARAMS.EMPTY_CART}=true`
  );

  if (
    orderType === OrderTypesEnum.DELIVERYV2 &&
    currentDeliveryConfig?.usesMap &&
    !deliveryAddress.selectedAddress?.id
  ) {
    stopLoading();
    dispatch(setAddressSelectionDrawerState(true));
    router.push(
      getPageLink(router, PagesEnum.Home).href,
      getPageLink(router, PagesEnum.Home).as
    );
    dispatch(
      setAnimatedGlobalBannerState({
        isOpen: true,
        text: "Please select an address",
        warning: true,
      })
    );

    return;
  }

  const total = loyaltyResult.pricing.total;
  if (total === 0) {
    paymentMethod = PAYMENT_METHOD.NONE;
  }

  const deviceNumber = LS._get(LocalStorageEnum.DEVICE_NUMBER);

  const franchiseSlug = getFranchiseSlug(franchise);

  const selectedFranchise: Franchise | null =
    restaurantInfo?.franchises?.find((f) => f.slug === franchiseSlug) || null;

  let validatedTableNumber = tableNumber;
  if (selectedFranchise) {
    const isValidTable = validateTableForFranchise(
      tableNumber,
      selectedFranchise,
      franchiseSlug
    );

    if (!isValidTable) {
      validatedTableNumber = 1;
      console.warn(
        `Table ${tableNumber} does not exist for franchise ${franchiseSlug}. Using default table 1.`
      );
    }
  } else {
    validatedTableNumber = 1;
  }

  const requestBody: CreateReceiptBodyTypeFlat = {
    items: buildCreateReceiptItems(cartItems, loyaltyResult),
    tableNumber: validatedTableNumber,
    // Backend expects the enum KEY ("TAKE_OUT"), not the FE value ("takeout-on-table").
    orderType: getOrderTypeKeyStringByValue(orderType),
    restaurantId: restaurantInfo?.id || parseInt(env.restaurantId!),
    franchiseSlug,
    receiptNote:
      currentDeliveryConfig?.deliveryType?.id === 1 ? undefined : receiptNote,
    paymentMethod,
    customerAddressId: currentDeliveryConfig?.usesMap
      ? deliveryAddress.selectedAddress?.id
      : undefined,
    franchiseDeliveryConfigId: currentDeliveryConfig?.id,
    pricing: mapCartPricing(loyaltyResult),
    scheduledSlotId,
    numberOfGuests,
    scheduledPickup,
    deviceNumber: deviceNumber ? Number(deviceNumber) : undefined,
    source: selfService
      ? ReceiptSourceEnum.SELF_SERVICE
      : ReceiptSourceEnum.WEB,
    sourceType: ReceiptSourceTypeEnum.GUEST,
    appliedOffer,
    ...(useWallet ? { useWallet: true } : {}),
    ...(payWithPartnerPoints ? { payWithPartnerPoints: true } : {}),
    ...(paymentMethod === PAYMENT_METHOD.ONLINE_CARD
      ? {
          tips,
          success_url,
          cancel_url,
          cardSubtype: savedCard?.cardSubtype,
          maskedPan: savedCard?.maskedPan,
          onlinePaymentProvider,
          // Tap web hosted checkout (renders all MID-enabled methods); ignored
          // by the other providers.
          tapSource: "src_all",
        }
      : {}),
  };

  // Persist the points-to-earn from the last calculate so the pay screen can
  // show them — the cart (and its calculate result) is reset before /pay renders.
  dispatch(setLoyaltyEarnedPoints(loyaltyResult.points?.earned?.total));

  await confirmReceiptRequest(
    requestBody,
    dispatch,
    referenceName,
    router,
    redirectPage,
    undefined,
    selfService,
    loadingStateSolution,
    { useV2: true }
  );
};
